import { RubyGemMark } from '../components/RubyGemMark';
import { RubyLoader } from '../components/RubyLoader';
import React, { useEffect, useMemo, useState } from 'react';
import {
  DollarSign, AlertTriangle, Building, MapPin, RefreshCw, Search,
  ArrowUpRight, ShieldCheck, Clock, Ban, CheckCircle2, Tag, Layers, X, Save, Zap,
} from 'lucide-react';
import { api } from '../services/apiService';
import { billingApi, billedInternally, countSkips, needsStripePrice, type SyncPricesResult } from '../services/billingApi';
import { toastService } from '../services/toastService';
import TenantFinanceDetail from '../components/finance/TenantFinanceDetail';
import type { Tenant } from '../types';

/**
 * FINANCE HUB — one place to see every dollar across all tenants.
 *
 * Reads live data only (tenants, deals, location billing requests). Sections for
 * Ruby Tokens / SKU catalog / device financing are intentionally absent until the
 * billing-service backend exists; see
 * docs/superpowers/specs/2026-08-21-unified-billing-design.md
 */

type Lens = 'status' | 'location' | 'product' | 'deal';

interface LocationRequest {
  tenantId: string;
  requestId: string;
  name?: string;
  status?: string;
  isFree?: boolean;
  priceAmount?: number;
  monthlyAmount?: number;
}

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Health of a tenant for billing purposes. Mirrors tenantAccessState in shared-types. */
function billingState(t: Tenant): 'active' | 'grace' | 'suspended' | 'trialing' | 'cancelled' {
  if (t.status === 'suspended') return 'suspended';
  if (t.status === 'grace') {
    if (typeof t.graceUntil === 'number' && t.graceUntil > 0 && t.graceUntil <= Date.now()) return 'suspended';
    return 'grace';
  }
  const s = (t.subscriptionStatus || '').toLowerCase();
  if (s === 'trialing' || s === 'affiliate_trial') return 'trialing';
  if (s === 'past_due' || s === 'unpaid' || s === 'incomplete') return 'grace';
  if (s === 'canceled' || s === 'cancelled') return 'cancelled';
  return 'active';
}

const STATE_STYLE: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  trialing: 'bg-sky-100 text-sky-700',
  grace: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
};

const FinanceHub: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [locations, setLocations] = useState<LocationRequest[]>([]);
  const [skus, setSkus] = useState<any[]>([]);
  const [stripePrices, setStripePrices] = useState<any[]>([]);
  const [editing, setEditing] = useState<any | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [lens, setLens] = useState<Lens>('status');
  const [q, setQ] = useState('');
  const [generatingSkuId, setGeneratingSkuId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncPricesResult | null>(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      // Each source is independent — one failing must not blank the whole page.
      const [tRes, dRes, lRes, sRes, pRes] = await Promise.allSettled([
        api.tenants.getAll(),
        api.deals.list(),
        api.locationBilling.listAllRequests(),
        api.billingCatalog.listSkus(),
        api.superAdmin.listPrices(),
      ]);
      setStripePrices(pRes?.status === 'fulfilled' ? ((pRes.value as any)?.items ?? []) : []);
      setSkus(sRes.status === 'fulfilled' ? ((sRes.value as any)?.items ?? []) : []);
      setTenants(tRes.status === 'fulfilled' && Array.isArray(tRes.value) ? (tRes.value as Tenant[]) : []);
      setDeals(dRes.status === 'fulfilled' ? ((dRes.value as any)?.deals ?? []) : []);
      const locItems = lRes.status === 'fulfilled'
        ? ((lRes.value as any)?.items ?? (lRes.value as any)?.requests ?? [])
        : [];
      setLocations(Array.isArray(locItems) ? locItems : []);
      if (tRes.status === 'rejected') setErr('Could not load tenants — figures below are incomplete.');
    } catch (e: any) {
      setErr(e?.message || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /** Mints a Stripe price for one SKU. Errors from the server (e.g. a zero-amount
   *  SKU refusal) are shown verbatim — they are more useful than a generic message.
   *
   *  A 200 is NOT proof a price was generated. A SKU whose type never carries a
   *  Stripe price (§13/B2 — anything but `device_finance`) comes back
   *  `skipped: true` with a reason, and claiming success there would tell the
   *  operator that money infrastructure changed when nothing happened. */
  const handleGeneratePrice = async (skuId: string) => {
    setGeneratingSkuId(skuId);
    try {
      const res = await billingApi.generatePrice(skuId);
      if (res.skipped) {
        toastService.info(res.reason || `${skuId} is not priced in Stripe — it is billed internally.`);
      } else {
        toastService.success(`Stripe price generated for ${skuId}`);
      }
      await load();
    } catch (e: any) {
      toastService.error(e?.message || `Failed to generate a price for ${skuId}`);
    } finally {
      setGeneratingSkuId(null);
    }
  };

  /** Bulk-generates prices for every active, unpriced SKU. A non-empty `failed[]`
   *  is a partial failure and must stay visible — never collapsed into a success
   *  toast — so the result is kept in state and rendered until dismissed. */
  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await billingApi.syncPrices();
      setSyncResult(res);
      if (res.failed.length > 0) {
        toastService.error(
          `Sync finished with ${res.failed.length} failure${res.failed.length === 1 ? '' : 's'} — see details below.`,
        );
      } else {
        // "already priced" and "never priced in Stripe" are different facts and
        // must not be reported as one number.
        const { notPriceable, alreadyPriced } = countSkips(res);
        toastService.success(
          `Synced ${res.created.length} price${res.created.length === 1 ? '' : 's'}` +
          (alreadyPriced ? `, ${alreadyPriced} already priced` : '') +
          (notPriceable ? `, ${notPriceable} billed internally (no Stripe price)` : ''),
        );
      }
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const stats = useMemo(() => {
    const byState: Record<string, Tenant[]> = { active: [], trialing: [], grace: [], suspended: [], cancelled: [] };
    let subMrr = 0;
    for (const t of tenants) {
      const st = billingState(t);
      (byState[st] ||= []).push(t);
      if (st === 'active' || st === 'grace' || st === 'trialing') subMrr += t.subscriptionCost || 0;
    }
    const atRisk = [...byState.grace, ...byState.suspended]
      .reduce((sum, t) => sum + (t.subscriptionCost || 0), 0);

    const activeLocations = locations.filter(l => (l.status || 'active') === 'active');
    const locMrr = activeLocations.reduce((s, l) => s + (l.isFree ? 0 : (l.priceAmount || l.monthlyAmount || 0)), 0);

    return { byState, subMrr, atRisk, locMrr, activeLocations: activeLocations.length };
  }, [tenants, locations]);

  const dunning = useMemo(() => {
    return tenants
      .map(t => ({ t, state: billingState(t) }))
      .filter(x => x.state === 'grace' || x.state === 'suspended')
      .sort((a, b) => (a.t.graceUntil || 0) - (b.t.graceUntil || 0));
  }, [tenants]);

  const lensRows = useMemo(() => {
    const groups = new Map<string, { label: string; tenants: number; amount: number }>();
    const dealByCode = new Map(deals.map(d => [d.code, d]));

    // Location lens reads the location-billing requests directly — that is where
    // per-store revenue actually lives, not on the tenant record.
    if (lens === 'location') {
      const byTenant = new Map(tenants.map(t => [t.id, t]));
      for (const l of locations) {
        if ((l.status || 'active') === 'inactive') continue;
        const t = byTenant.get(l.tenantId);
        const label = `${l.name || l.requestId}${t ? ` · ${t.name || t.businessName || ''}` : ''}`;
        const amount = l.isFree ? 0 : (l.priceAmount || l.monthlyAmount || 0);
        groups.set(l.requestId, { label, tenants: 1, amount });
      }
      return [...groups.entries()].map(([k, v]) => ({ key: k, ...v })).sort((a, b) => b.amount - a.amount);
    }

    for (const t of tenants) {
      const st = billingState(t);
      let key: string = st;
      let label: string = st;
      if (lens === 'product') {
        key = t.productType || 'unassigned';
        label = key;
      } else if (lens === 'deal') {
        const code = (t as any).dealCode || (t as any).deal || '';
        key = code || 'no-deal';
        label = code ? (dealByCode.get(code)?.name || code) : 'No deal / direct';
      }
      const cur = groups.get(key) || { label, tenants: 0, amount: 0 };
      cur.tenants += 1;
      if (st === 'active' || st === 'grace' || st === 'trialing') cur.amount += t.subscriptionCost || 0;
      groups.set(key, cur);
    }
    return [...groups.entries()]
      .map(([k, v]) => ({ key: k, ...v }))
      .sort((a, b) => b.amount - a.amount);
  }, [tenants, deals, lens, locations]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return tenants;
    return tenants.filter(t =>
      (t.name || t.businessName || '').toLowerCase().includes(needle) ||
      (t.id || '').toLowerCase().includes(needle) ||
      (t.ownerEmail || '').toLowerCase().includes(needle));
  }, [tenants, q]);

  const totalLens = lensRows.reduce((s, r) => s + r.amount, 0);

  return (
    // index.html puts `text-white` on <body>, so this light page states its own
    // text colour on the CONTAINER — otherwise any child that does not set one
    // renders white-on-white.
    <div className="min-h-screen bg-gray-100 text-gray-900">
      {/* Header — Purchase Orders pattern: black banner */}
      <header className="bg-black text-white px-6 py-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-[#DC143C] inline-flex items-center justify-center">
                <DollarSign size={18} />
              </span>
              <h1 className="text-xl font-semibold">Finance Hub</h1>
            </div>
            <p className="text-gray-400 text-sm mt-1">
              Every subscription across all tenants · reachable from any tenant page
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="bg-white/10 hover:bg-white/20 border border-white/15 rounded-lg px-3 py-2 text-sm flex items-center gap-2 min-h-[44px] disabled:opacity-50"
          >
            {loading ? <RubyGemMark size={15} drawing color="currentColor" /> : <RefreshCw size={15} />}
            Refresh
          </button>
        </div>
      </header>

      {err && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 text-sm text-amber-900 flex items-center gap-2">
          <AlertTriangle size={15} /> {err}
        </div>
      )}

      {dunning.length === 0 && !loading && !err && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-2.5 flex items-center gap-2 text-sm text-emerald-900">
          <ShieldCheck size={15} />
          <strong>All clear.</strong> No tenant is in grace or suspended for non-payment.
        </div>
      )}

      {selected ? (
        <div className="p-6">
          <TenantFinanceDetail
            tenant={selected}
            locations={locations}
            stripePrices={stripePrices}
            skus={skus}
            onBack={() => setSelected(null)}
            onChanged={load}
          />
        </div>
      ) : (
      <div className="p-6 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Subscription MRR" value={money(stats.subMrr)} sub={`${tenants.length} tenants`} icon={<DollarSign size={16} />} />
          <Kpi label="Location MRR" value={money(stats.locMrr)} sub={`${stats.activeLocations} active locations`} icon={<MapPin size={16} />} />
          <Kpi label="Paying tenants" value={String(stats.byState.active.length + stats.byState.trialing.length)}
               sub={`${stats.byState.trialing.length} on trial`} icon={<Building size={16} />} />
          <Kpi label="At risk" value={money(stats.atRisk)} sub={`${dunning.length} in grace / suspended`}
               icon={<AlertTriangle size={16} />} danger={dunning.length > 0} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          {/* Revenue by lens */}
          <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold text-sm">Revenue breakdown</h2>
              <div className="flex gap-1 text-xs">
                {(['status', 'location', 'product', 'deal'] as Lens[]).map(l => (
                  <button
                    key={l}
                    onClick={() => setLens(l)}
                    className={`px-3 py-1.5 rounded-full capitalize min-h-[36px] ${
                      lens === l ? 'bg-emerald-600 text-white font-medium' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {l === 'deal' && <Tag size={12} className="inline mr-1 -mt-0.5" />}
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">{lens}</th>
                    <th className="px-4 py-2 text-right">{lens === 'location' ? 'Stores' : 'Tenants'}</th>
                    <th className="px-4 py-2 text-right">MRR</th>
                    <th className="px-4 py-2 text-right">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lensRows.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                      {loading ? <RubyLoader variant="section" /> : 'No data'}
                    </td></tr>
                  )}
                  {lensRows.map(r => (
                    <tr key={r.key} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        {lens === 'status'
                          ? <span className={`px-2 py-0.5 rounded-full text-xs ${STATE_STYLE[r.key] || 'bg-gray-100 text-gray-600'}`}>{r.label}</span>
                          : <span className="font-medium">{r.label}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{r.tenants}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(r.amount)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">
                        {totalLens > 0 ? `${((r.amount / totalLens) * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-900 text-white">
                  <tr>
                    <td className="px-4 py-2.5 font-semibold">Total</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{tenants.length}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{money(totalLens)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Dunning queue */}
          <div className={`bg-white rounded-xl border ${dunning.length ? 'border-amber-300 ring-1 ring-amber-100' : 'border-gray-200'}`}>
            <div className={`px-4 py-3 border-b rounded-t-xl ${dunning.length ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}`}>
              <h2 className={`font-semibold text-sm ${dunning.length ? 'text-amber-900' : ''}`}>
                Dunning queue {dunning.length > 0 && `(${dunning.length})`}
              </h2>
            </div>
            <div className="divide-y divide-gray-100 text-sm max-h-[420px] overflow-y-auto">
              {dunning.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-400 flex flex-col items-center gap-2">
                  <CheckCircle2 size={22} className="text-emerald-500" />
                  Nobody is behind on payment.
                </div>
              )}
              {dunning.map(({ t, state }) => {
                const days = t.graceUntil ? Math.ceil((t.graceUntil - Date.now()) / 86400000) : null;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelected(t)}
                    className="w-full text-left px-4 py-2.5 hover:bg-amber-50/60 flex items-center justify-between gap-3 min-h-[44px]"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{t.name || t.businessName || t.id}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        {state === 'suspended'
                          ? <><Ban size={11} /> suspended</>
                          : <><Clock size={11} /> grace{days !== null && days > 0 ? ` · ${days}d left` : ' · expired'}</>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="tabular-nums font-medium">{money(t.subscriptionCost || 0)}</div>
                      <span className={`px-1.5 py-0.5 rounded text-[11px] ${STATE_STYLE[state]}`}>{state}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* All tenants */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-semibold text-sm">
              All tenants <span className="text-gray-400 font-normal">— every row opens that tenant's billing</span>
            </h2>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search tenant, id, email…"
                className="border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm w-72 min-h-[40px]"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Tenant</th>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-right">Packages</th>
                  <th className="px-4 py-2 text-right">MRR</th>
                  <th className="px-4 py-2 text-left">Renews</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    {loading ? <RubyLoader variant="section" /> : 'No tenants match'}
                  </td></tr>
                )}
                {filtered.map(t => {
                  const state = billingState(t);
                  return (
                    <tr
                      key={t.id}
                      onClick={() => setSelected(t)}
                      className="hover:bg-[#DC143C]/5 cursor-pointer"
                    >
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-[#DC143C]">{t.name || t.businessName || '—'}</div>
                        <div className="text-[11px] text-gray-400">{t.id}</div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{t.productType || '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{t.enabledPackages?.length ?? 0}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium">{money(t.subscriptionCost || 0)}</td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {t.currentPeriodEnd ? new Date(t.currentPeriodEnd).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATE_STYLE[state]}`}>{state}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-300"><ArrowUpRight size={15} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Catalog — live from billing-service */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-semibold text-sm flex items-center gap-2">
              <Layers size={15} className="text-gray-500" />
              Price catalog
              <span className="text-gray-400 font-normal">— the single definition of a price</span>
            </h2>
            <div className="flex items-center gap-3">
              <div className="text-xs text-gray-500">
                {skus.length} SKUs · {skus.filter(s => s.scope === 'location').length} per-location ·{' '}
                {skus.filter(s => s.scope === 'tenant').length} business-wide
                {skus.some(needsStripePrice) && (
                  <span className="text-red-600 font-medium"> · {skus.filter(needsStripePrice).length} without a Stripe price</span>
                )}
                {skus.some(billedInternally) && (
                  <span> · {skus.filter(billedInternally).length} billed internally</span>
                )}
              </div>
              <button
                onClick={handleSyncAll}
                disabled={syncing || skus.length === 0}
                className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg px-3 py-2 text-xs font-medium flex items-center gap-1.5 min-h-[36px] disabled:opacity-50"
              >
                {syncing ? <RubyGemMark size={13} drawing color="currentColor" /> : <RefreshCw size={13} />}
                {syncing ? 'Syncing…' : 'Sync all'}
              </button>
            </div>
          </div>
          {syncResult && (
            <div className={`px-4 py-3 border-b text-sm ${syncResult.failed.length > 0 ? 'bg-red-50 border-red-200 text-red-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium flex items-center gap-1.5">
                    {syncResult.failed.length > 0
                      ? <><AlertTriangle size={14} /> Sync completed with {syncResult.failed.length} failure{syncResult.failed.length === 1 ? '' : 's'}</>
                      : <><CheckCircle2 size={14} /> Sync completed successfully</>}
                  </p>
                  <p className="text-xs mt-0.5 opacity-80">
                    {syncResult.created.length} created
                    {` · ${countSkips(syncResult).alreadyPriced} already priced`}
                    {countSkips(syncResult).notPriceable > 0 &&
                      ` · ${countSkips(syncResult).notPriceable} billed internally (never priced in Stripe)`}
                    {countSkips(syncResult).deprecated > 0 && ` · ${countSkips(syncResult).deprecated} deprecated`}
                    {syncResult.failed.length > 0 && ` · ${syncResult.failed.length} failed`}
                  </p>
                  {countSkips(syncResult).notPriceable > 0 && (
                    <ul className="mt-2 space-y-1">
                      {(syncResult.skippedDetail || [])
                        .filter(d => d.code === 'not_priceable')
                        .map(d => (
                          <li key={d.skuId} className="text-xs opacity-80">
                            <span className="font-mono">{d.skuId}</span> — {d.reason}
                          </li>
                        ))}
                    </ul>
                  )}
                  {syncResult.failed.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {syncResult.failed.map(f => (
                        <li key={f.skuId} className="text-xs text-red-800">
                          <span className="font-mono">{f.skuId}</span> — {f.reason}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button onClick={() => setSyncResult(null)} className="text-current opacity-60 hover:opacity-100 min-h-[32px] px-1 shrink-0">
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
          {skus.length === 0 ? (
            loading ? (
              <RubyLoader variant="section" />
            ) : (
              <p className="px-4 py-6 text-center text-sm text-gray-400">
                billing-service unreachable or catalog empty.
              </p>
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2 text-left">SKU</th>
                    <th className="px-4 py-2 text-left">Scope</th>
                    <th className="px-4 py-2 text-left">On non-payment</th>
                    <th className="px-4 py-2 text-right">Price</th>
                    <th className="px-4 py-2 text-left">Stripe price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {skus.map(s => (
                    <tr key={s.skuId} onClick={() => { setEditing({ ...s }); setSaveErr(null); }}
                        className="hover:bg-[#DC143C]/5 cursor-pointer">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-900">{s.name}</div>
                        <div className="text-[11px] text-gray-400">{s.skuId}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          s.scope === 'tenant' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'
                        }`}>{s.scope === 'tenant' ? 'business-wide' : 'per-location'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          s.enforcement === 'suspend_all' ? 'bg-red-100 text-red-700' :
                          s.enforcement === 'collections_only' ? 'bg-sky-100 text-sky-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{s.enforcement.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                        {s.amountCents ? money(s.amountCents) : <span className="text-gray-400">not priced</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        {s.hasPrice ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                            <CheckCircle2 size={12} />
                            {s.stripePriceId?.dev || 'Priced'}
                          </span>
                        ) : billedInternally(s) ? (
                          // Correct state, not a gap: software is billed as one
                          // managed line per location at the net we compute, so
                          // Stripe never holds a price for this SKU (§13/B2).
                          // No red badge, and no "Generate" button that would
                          // come back skipped.
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600"
                            title="Billed internally — only device SKUs carry a Stripe price."
                          >
                            <ShieldCheck size={11} /> Billed internally
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                              <AlertTriangle size={11} /> No Stripe price
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleGeneratePrice(s.skuId); }}
                              disabled={generatingSkuId === s.skuId}
                              className="text-xs font-medium text-white bg-[#DC143C] hover:bg-[#c01236] rounded-md px-2 py-1 min-h-[28px] inline-flex items-center gap-1 disabled:opacity-50"
                            >
                              <Zap size={11} />
                              {generatingSkuId === s.skuId ? 'Generating…' : 'Generate'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* SKU editor — the ONE place a price is set. No native dialogs. */}
        {editing && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{editing.name}</h3>
                  <p className="text-[11px] text-gray-400">{editing.skuId}</p>
                </div>
                <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700 min-h-[44px] px-2"><X size={18} /></button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Monthly price (USD)</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={(editing.amountCents ?? 0) / 100}
                    onChange={e => setEditing({ ...editing, amountCents: Math.round(parseFloat(e.target.value || '0') * 100) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px] tabular-nums"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Stripe price (dev)</label>
                  <select
                    value={editing.stripePriceId?.dev || ''}
                    onChange={e => setEditing({ ...editing, stripePriceId: { ...(editing.stripePriceId || { dev: '', prod: '' }), dev: e.target.value } })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px]"
                  >
                    <option value="">— not attached —</option>
                    {stripePrices.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.nickname || p.productName || p.id} — ${((p.unitAmount ?? 0) / 100).toFixed(2)}/{p.interval}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Stripe prices are immutable. Changing the amount above does not change Stripe —
                    attach a different price here.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Scope</label>
                    <select
                      value={editing.scope}
                      onChange={e => setEditing({ ...editing, scope: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px]"
                    >
                      <option value="location">per-location</option>
                      <option value="tenant">business-wide</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">On non-payment</label>
                    <select
                      value={editing.enforcement}
                      onChange={e => setEditing({ ...editing, enforcement: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px]"
                    >
                      <option value="suspend_feature">suspend feature</option>
                      <option value="suspend_all">suspend all</option>
                      <option value="collections_only">collections only</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Ruby Tokens granted monthly</label>
                  <input
                    type="number" min="0" step="100"
                    value={editing.tokenGrantMonthly ?? 0}
                    onChange={e => setEditing({ ...editing, tokenGrantMonthly: parseInt(e.target.value || '0', 10) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px] tabular-nums"
                  />
                </div>

                {editing.scope === 'tenant' && (
                  <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                    Business-wide: charged once no matter how many stores.
                  </p>
                )}
                {saveErr && (
                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveErr}</p>
                )}
              </div>

              <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button onClick={() => setEditing(null)}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm min-h-[44px]">Cancel</button>
                <button
                  disabled={saving}
                  onClick={async () => {
                    try {
                      setSaving(true); setSaveErr(null);
                      await api.billingCatalog.upsertSku(editing);
                      setEditing(null);
                      await load();
                    } catch (e: any) {
                      setSaveErr(e?.message || 'Save failed');
                    } finally { setSaving(false); }
                  }}
                  className="px-4 py-2 rounded-lg bg-[#DC143C] text-white text-sm font-medium min-h-[44px] inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <Save size={15} /> {saving ? 'Saving…' : 'Save price'}
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 px-1">
          Click any tenant to manage their locations, devices and billing without leaving this page.
        </p>
      </div>
      )}
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: string; sub?: string; icon?: React.ReactNode; danger?: boolean }> =
  ({ label, value, sub, icon, danger }) => (
  <div className={`bg-white rounded-xl border p-4 ${danger ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-200'}`}>
    <div className={`text-[11px] uppercase tracking-wide flex items-center gap-1.5 ${danger ? 'text-amber-700' : 'text-gray-500'}`}>
      {icon}{label}
    </div>
    <div className={`text-2xl font-semibold tabular-nums mt-1 ${danger ? 'text-amber-700' : ''}`}>{value}</div>
    {sub && <div className={`text-xs mt-0.5 ${danger ? 'text-amber-700' : 'text-gray-500'}`}>{sub}</div>}
  </div>
);

export default FinanceHub;
